import type {
  ListContextValue,
  ListData,
  ListItem,
  ListLazyLoadingInfos,
  ListProviderProps,
} from "./list.type";
import type {
  TableViewFilterKey,
  TableViewFilterValue,
  TableViewSort,
} from "@/entities/table-view";
import type {
  ListTableColumnKey,
  OnListTableChange,
} from "@/modules/list-table";

import get from "lodash/get";
import isEqual from "lodash/isEqual";
import { useState, useMemo } from "react";

import { TAB_PARAM_KEYS } from "@/constants/TabKeys";
import { convertTableParamsToListParams } from "@/modules/list-table";
import { useOrganizationContextListFilter } from "@/modules/organization/organization-context-list-filter";

import { useListParamsHandler } from "./list-params";
import { useListContext } from "./list.context";
import {
  convertFiltersToFetchFilters,
  getListCleanFetchParams,
} from "./list.helper";

export const useListParams = () => {
  const { listParams } = useListContext();

  return listParams;
};

export const useListFilterValues = (filterKey: TableViewFilterKey) => {
  const { listParams } = useListContext();

  return useMemo(
    () => get(listParams, `filters.${filterKey}`, []) as TableViewFilterValue[],
    [listParams?.filters, filterKey]
  );
};

export const useListMetadata = <Metadata = unknown>(
  path: string,
  defaultValue: Metadata
) => {
  const { metadata } = useListContext();

  return useMemo(
    () => get(metadata, path, defaultValue) as Metadata,
    [metadata, path, defaultValue]
  );
};

export const useListContextValues = <
  DataItem extends ListItem = ListItem,
  Metadata = unknown,
>({
  endpoint = "",
  storageKey,
  contextTabValue,
  contextTabKey = TAB_PARAM_KEYS.CONTEXT_TAB_KEY,
  includes = [],
  withMetadata = [],
  defaultSort = ["-updated_at"],
  multipleSort = true,
  hiddenFilters = {},
  withContext = false,
  loading = false,
  enabled = true,
  neverStale = false,
  parentId = null,
  lazy = false,
  pageSize = 10,
}: ListProviderProps): ListContextValue<DataItem, Metadata> => {
  const { contextFilter } = useOrganizationContextListFilter({ withContext });

  const [tableColumnsKeys, _setColumnsKeys] = useState<ListTableColumnKey[]>(
    []
  );
  const tableColumnsCount = tableColumnsKeys?.length ?? 0;

  const [listData, _setListData] = useState<ListData<DataItem, Metadata>>({
    loading: false,
    fetching: false,
    data: [],
    total: 0,
    metadata: {},
  });

  const [listLazyInfos, setListLazyInfos] = useState<ListLazyLoadingInfos>({
    hasNextPage: false,
    fetchNextPage: undefined,
  });

  const {
    sort,
    filters,
    pagination,
    clearFilter,
    clearFilters,
    clearFiltersAndSorts,
    resetFiltersAndSorts,
    updateFilterValues,
    updateSearchParams,
  } = useListParamsHandler({
    storageKey,
    contextTabKey,
    contextTabValue,
    defaultSort,
    defaultPageSize: pageSize,
  });

  const setListData = (data: ListData<DataItem, Metadata>) => {
    if (!isEqual(data, listData)) _setListData(data);
  };

  const setTableColumnsKeys = (columnsKeys: ListTableColumnKey[]) => {
    if (!isEqual(tableColumnsKeys, columnsKeys)) _setColumnsKeys(columnsKeys);
  };

  const fetchParams = useMemo(() => {
    const { current: pageNumber, pageSize } = pagination;
    return getListCleanFetchParams({
      include: includes.join(","),
      with: withMetadata.join(","),
      sort: sort.length > 0 ? sort.join(",") : defaultSort, // always fallback to `defaultSort` to avoid api random sorting
      ...hiddenFilters,
      ...contextFilter,
      ...convertFiltersToFetchFilters(filters),
      "page[number]": pageNumber,
      "page[size]": pageSize,
    });
  }, [
    includes,
    withMetadata,
    hiddenFilters,
    contextFilter,
    defaultSort,
    sort,
    filters,
    pagination,
  ]);

  const onListTableChange: OnListTableChange<DataItem> = (
    tablePagination,
    tableFilters,
    tableSort
  ) => {
    // Always fallback to empty array (and never defaultSort), to let the user:
    // - remove all sort params if needed
    // - have just 1 sort param at a time (usefull when there is multiple items in defaultSort)
    const defaultSort: TableViewSort = [];

    const newParams = convertTableParamsToListParams({
      tableSort,
      tableFilters,
      tablePagination,
      currentListSort: sort,
      defaultPageSize: pageSize,
      defaultSort,
    });

    updateSearchParams(newParams);
  };

  const onFetchNextPage = () => {
    const { hasNextPage, fetchNextPage } = listLazyInfos;
    const { loading } = listData;
    if (hasNextPage && !loading) fetchNextPage?.();
  };

  return {
    // external params
    endpoint,
    includes,
    withMetadata,
    defaultSort,
    multipleSort,
    enabled,
    neverStale,
    parentId,
    lazy,
    // internal params
    listParams: { filters, sort, pagination },
    fetchParams,
    ...listData,
    loading: loading || listData.loading,
    setListData,
    setListLazyInfos,
    tableColumnsCount,
    tableColumnsKeys,
    setTableColumnsKeys,
    // external fn & callbacks
    clearFilter,
    clearFilters,
    clearFiltersAndSorts,
    resetFiltersAndSorts,
    onFilterChange: updateFilterValues,
    onListTableChange,
    onFetchNextPage,
  };
};
